import React, { useEffect, useState, useCallback } from "react";
import {
  Sparkles, RefreshCcw, TrendingUp, ArrowRight, AlertCircle, Info,
  Layers, ChevronRight, Clock, Target, Zap, CheckCircle2, History, Trash2,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { getIdeas, refreshIdeas, recordSwapExecuted, getSwapExecutions, deleteSwapExecution } from "../lib/api";

const fmtCr = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)}L Cr`;
  if (n >= 1000) return `₹${(n / 1000).toFixed(1)}K Cr`;
  return `₹${Math.round(n)} Cr`;
};
const fmtPct = (n, d = 1) => (n === null || n === undefined ? "—" : `${n >= 0 ? "+" : ""}${n.toFixed(d)}%`);
const fmtPrice = (n) => (n ? `₹${n.toLocaleString("en-IN")}` : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "never");

function scoreColor(score) {
  if (score >= 75) return "text-emerald-400";
  if (score >= 60) return "text-green-400";
  if (score >= 45) return "text-amber-400";
  return "text-orange-400";
}

export default function IdeasPage({ onStockClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tab, setTab] = useState("picks"); // picks | swaps | executed
  const [executions, setExecutions] = useState([]);
  const [execDialogSwap, setExecDialogSwap] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await getIdeas();
      setData(data);
    } catch {
      toast.error("Failed to load ideas");
    } finally {
      setLoading(false);
    }
    try {
      const { data: exec } = await getSwapExecutions();
      setExecutions(exec.executions || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    toast.info("Generating fresh ideas — may take 60-90 seconds…");
    try {
      const { data } = await refreshIdeas();
      if (data.error) {
        toast.error(`Refresh failed: ${data.error}`);
      } else {
        toast.success(`${data.num_picks} picks · ${data.num_swaps} swap suggestions`);
        setData(data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-[1400px] mx-auto animate-pulse">
        <div className="h-8 w-48 bg-white/5 rounded mb-6" />
        <div className="h-32 bg-white/5 rounded" />
      </div>
    );
  }

  const empty = !data || data.empty || !data.picks?.length;

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-6" data-testid="ideas-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded bg-gradient-to-br from-amber-400/20 to-pink-500/20">
              <Sparkles size={18} className="text-amber-300" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Chivo" }}>
              AI Ideas
            </h1>
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-1 max-w-2xl">
            Auto-curated, aggressive but fundamentally strong picks for a <b>2-year horizon</b> — scored by quality, growth, momentum, entry, and red-flags. Powered by Tickertape + GPT-5.2.
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          className="bg-gradient-to-r from-amber-500 to-pink-500 hover:opacity-90 text-white rounded text-sm"
          data-testid="refresh-ideas-btn"
        >
          <RefreshCcw size={14} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Generating…" : empty ? "Generate ideas" : "Refresh ideas"}
        </Button>
      </div>

      {empty && !refreshing && (
        <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-10 text-center" data-testid="ideas-empty">
          <Sparkles size={32} className="mx-auto text-amber-400 mb-3 opacity-70" />
          <p className="text-white font-medium">No ideas generated yet</p>
          <p className="text-xs text-[var(--text-muted)] mt-1 max-w-md mx-auto">
            Click <b>Generate ideas</b> above to pull a fresh universe from Tickertape, score each stock against your 2-year aggressive strategy, and get AI-written investment theses.
          </p>
        </div>
      )}

      {!empty && (
        <>
          {/* Meta bar */}
          <div className="flex items-center gap-4 text-xs text-[var(--text-muted)] flex-wrap" data-testid="ideas-meta">
            <span className="flex items-center gap-1.5"><Clock size={12} /> Generated: <b className="text-white/80">{fmtDate(data.generated_at)}</b></span>
            <span className="flex items-center gap-1.5"><Layers size={12} /> Universe: <b className="text-white/80">{data.universe_size}</b> stocks</span>
            <span className="flex items-center gap-1.5"><Target size={12} /> You hold: <b className="text-white/80">{data.excluded_held}</b> (excluded)</span>
            <span className="flex items-center gap-1.5"><Zap size={12} /> Min swap delta: <b className="text-white/80">{data.config?.swap_delta_threshold}</b> pts</span>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10">
            <TabButton active={tab === "picks"} onClick={() => setTab("picks")} count={data.picks.length} testid="tab-picks">
              Top Ideas
            </TabButton>
            <TabButton active={tab === "swaps"} onClick={() => setTab("swaps")} count={data.swaps.length} testid="tab-swaps">
              Swap Suggestions
            </TabButton>
            <TabButton active={tab === "executed"} onClick={() => setTab("executed")} count={executions.length} testid="tab-executed">
              Executed
            </TabButton>
          </div>

          {tab === "picks" && (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="picks-grid">
              {data.picks.map((p) => (
                <PickCard key={p.symbol} pick={p} onClick={() => onStockClick?.(p.symbol)} />
              ))}
            </div>
          )}

          {tab === "swaps" && (
            <div className="space-y-3" data-testid="swaps-list">
              {data.swaps.length === 0 ? (
                <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-8 text-center text-[var(--text-muted)] text-sm">
                  No swap suggestions — your current holdings already score higher than the new ideas (min Δ{data.config?.swap_delta_threshold}).
                </div>
              ) : (
                data.swaps.map((s, i) => (
                  <SwapRow
                    key={i}
                    swap={s}
                    onClickFrom={() => onStockClick?.(s.from_symbol)}
                    onClickTo={() => onStockClick?.(s.to_symbol)}
                    onMarkExecuted={() => setExecDialogSwap({ ...s, ideas_run_id: data.id })}
                  />
                ))
              )}
            </div>
          )}

          {tab === "executed" && (
            <ExecutionsList
              executions={executions}
              onDelete={async (id) => {
                if (!window.confirm("Delete this execution record?")) return;
                try {
                  await deleteSwapExecution(id);
                  toast.success("Deleted");
                  load();
                } catch { toast.error("Delete failed"); }
              }}
              onStockClick={onStockClick}
            />
          )}

          {execDialogSwap && (
            <MarkExecutedDialog
              swap={execDialogSwap}
              onClose={() => setExecDialogSwap(null)}
              onSaved={() => { setExecDialogSwap(null); load(); setTab("executed"); }}
            />
          )}
        </>
      )}

      {/* Methodology footnote */}
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-4 text-[11px] text-[var(--text-muted)] flex items-start gap-2" data-testid="methodology">
        <Info size={13} className="mt-0.5 flex-shrink-0 text-[var(--brand)]" />
        <div className="leading-relaxed">
          <b className="text-white/90">Methodology:</b> Universe from Tickertape screener (₹2K-50K Cr market cap, ROE&gt;15%, PE 0-120).
          Each stock scored 0-100 on <b>Quality</b> (25%) · <b>Growth</b> (30%) · <b>Momentum</b> (20%) · <b>Entry</b> (15%) − <b>Red Flags</b> (10%). Swap suggested when new pick scores at least 10 points higher than current holding, with same-sector preference and max 3 swap targets per pick. This is <b>not investment advice</b> — validate every idea yourself.
        </div>
      </div>
    </div>
  );
}

function TabButton({ active, onClick, count, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
        active ? "text-white" : "text-[var(--text-muted)] hover:text-white"
      }`}
    >
      {children}
      <span className="ml-2 text-xs opacity-70">({count})</span>
      {active && <span className="absolute bottom-[-1px] left-0 right-0 h-0.5 bg-gradient-to-r from-amber-400 to-pink-500" />}
    </button>
  );
}

function PickCard({ pick, onClick }) {
  const scColor = scoreColor(pick.score);
  const entryTag = pick.scorecard_tags?.entry_point;
  const redTag = pick.scorecard_tags?.red_flags;
  return (
    <div
      className="bg-[var(--bg-surface)] border border-white/10 hover:border-white/20 rounded-lg p-4 transition-colors cursor-pointer"
      onClick={onClick}
      data-testid={`pick-${pick.symbol}`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono font-semibold text-white text-sm">{pick.symbol}</span>
            <ChevronRight size={14} className="text-[var(--text-muted)]" />
          </div>
          <p className="text-xs text-[var(--text-muted)] truncate mt-0.5">{pick.name}</p>
          <p className="text-[10px] text-[var(--text-muted)] mt-0.5">{pick.subindustry || pick.sector}</p>
        </div>
        <div className={`text-right`}>
          <div className={`font-mono text-2xl font-bold ${scColor}`}>{pick.score.toFixed(0)}</div>
          <div className="text-[9px] text-[var(--text-muted)] uppercase tracking-wider">Score /100</div>
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-4 gap-2 py-2 border-y border-white/5">
        <Metric label="MCap" value={fmtCr(pick.market_cap_cr)} />
        <Metric label="ROE" value={pick.roe ? `${pick.roe.toFixed(1)}%` : "—"} />
        <Metric label="PE" value={pick.pe ? pick.pe.toFixed(1) : "—"} />
        <Metric label="1Y" value={fmtPct(pick.ret1y)} tone={pick.ret1y >= 0 ? "positive" : "negative"} />
      </div>

      {/* Scorecard badges */}
      <div className="flex gap-1.5 flex-wrap mt-2">
        {entryTag && (
          <Badge variant="outline" className={`text-[9px] ${entryTag === "Good" ? "border-emerald-500/30 text-emerald-400" : entryTag === "Bad" ? "border-red-500/30 text-red-400" : "border-amber-500/30 text-amber-400"}`}>
            Entry: {entryTag}
          </Badge>
        )}
        {redTag && (
          <Badge variant="outline" className={`text-[9px] ${redTag === "Low" ? "border-emerald-500/30 text-emerald-400" : "border-red-500/30 text-red-400"}`}>
            Red flags: {redTag}
          </Badge>
        )}
      </div>

      {/* Thesis */}
      {pick.thesis && (
        <p className="text-xs text-[var(--text-secondary)] mt-3 leading-relaxed" data-testid={`thesis-${pick.symbol}`}>
          {pick.thesis}
        </p>
      )}
      {pick.risk && (
        <div className="mt-2 flex items-start gap-1.5">
          <AlertCircle size={11} className="text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-[11px] text-amber-200/90 leading-relaxed">{pick.risk}</p>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, tone }) {
  const toneCls = tone === "positive" ? "text-emerald-400" : tone === "negative" ? "text-red-400" : "text-white";
  return (
    <div>
      <div className="text-[9px] text-[var(--text-muted)] uppercase tracking-wider">{label}</div>
      <div className={`font-mono text-xs mt-0.5 ${toneCls}`}>{value}</div>
    </div>
  );
}

function SwapRow({ swap, onClickFrom, onClickTo, onMarkExecuted }) {
  return (
    <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-4" data-testid={`swap-${swap.from_symbol}-to-${swap.to_symbol}`}>
      <div className="flex items-center gap-3 flex-wrap md:flex-nowrap">
        {/* FROM */}
        <div
          className="flex-1 min-w-0 p-3 rounded border border-red-500/20 bg-red-500/5 cursor-pointer hover:bg-red-500/10"
          onClick={onClickFrom}
        >
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-wider text-red-300">Sell</span>
            <span className={`font-mono text-xs ${scoreColor(swap.from_score)}`}>{swap.from_score.toFixed(0)}</span>
          </div>
          <div className="font-mono text-sm text-white mt-1">{swap.from_symbol}</div>
          <p className="text-[10px] text-[var(--text-muted)] truncate">{swap.from_name || swap.from_sector}</p>
          <div className="text-[10px] text-[var(--text-muted)] mt-1">
            Pos: ₹{(swap.from_current_value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
          </div>
        </div>

        {/* Arrow + Delta */}
        <div className="flex flex-col items-center gap-1 px-2">
          <ArrowRight size={20} className="text-amber-400" />
          <div className="text-[10px] font-mono text-amber-400 font-bold">+{swap.delta}</div>
          {swap.same_sector && (
            <Badge variant="outline" className="text-[8px] border-emerald-500/30 text-emerald-400 mt-0.5">
              same sector
            </Badge>
          )}
        </div>

        {/* TO */}
        <div
          className="flex-1 min-w-0 p-3 rounded border border-emerald-500/20 bg-emerald-500/5 cursor-pointer hover:bg-emerald-500/10"
          onClick={onClickTo}
        >
          <div className="flex items-center justify-between">
            <span className="text-[9px] uppercase tracking-wider text-emerald-300">Buy</span>
            <span className={`font-mono text-xs ${scoreColor(swap.to_score)}`}>{swap.to_score.toFixed(0)}</span>
          </div>
          <div className="font-mono text-sm text-white mt-1">{swap.to_symbol}</div>
          <p className="text-[10px] text-[var(--text-muted)] truncate">{swap.to_name}</p>
          <div className="text-[10px] text-[var(--text-muted)] mt-1">
            {swap.to_sector} · ROE {swap.to_roe?.toFixed(1)}% · PE {swap.to_pe?.toFixed(1)}
          </div>
        </div>
      </div>
      {swap.thesis && (
        <div className="mt-3 text-xs text-[var(--text-secondary)] leading-relaxed border-t border-white/5 pt-2">
          <b className="text-white/90">Why {swap.to_symbol}:</b> {swap.thesis}
        </div>
      )}
      {onMarkExecuted && (
        <div className="mt-3 flex justify-end border-t border-white/5 pt-2">
          <button
            onClick={onMarkExecuted}
            className="text-[11px] flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
            data-testid={`mark-executed-${swap.from_symbol}-${swap.to_symbol}`}
          >
            <CheckCircle2 size={11} /> Mark as Executed
          </button>
        </div>
      )}
    </div>
  );
}

function MarkExecutedDialog({ swap, onClose, onSaved }) {
  const [soldQty, setSoldQty] = useState("");
  const [soldPrice, setSoldPrice] = useState("");
  const [boughtQty, setBoughtQty] = useState("");
  const [boughtPrice, setBoughtPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await recordSwapExecuted({
        ideas_run_id: swap.ideas_run_id || null,
        from_symbol: swap.from_symbol,
        to_symbol: swap.to_symbol,
        sold_quantity: parseFloat(soldQty) || null,
        sold_price: parseFloat(soldPrice) || null,
        bought_quantity: parseFloat(boughtQty) || null,
        bought_price: parseFloat(boughtPrice) || null,
        notes,
      });
      toast.success("Swap recorded — performance will be tracked");
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--bg-surface)] border border-white/10 text-white max-w-md" data-testid="mark-executed-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Chivo" }}>Mark Swap Executed</DialogTitle>
          <DialogDescription className="text-[var(--text-muted)] text-xs">
            Logs this swap so Capital360 can track {swap.to_symbol}'s performance vs holding onto {swap.from_symbol}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-[var(--text-muted)] tracking-wider mb-1 block">Sold {swap.from_symbol}</label>
              <div className="grid grid-cols-2 gap-1">
                <Input type="number" value={soldQty} onChange={(e) => setSoldQty(e.target.value)} placeholder="Qty" className="bg-[var(--bg-base)] border-white/10 text-white font-mono text-sm" data-testid="sold-qty" />
                <Input type="number" value={soldPrice} onChange={(e) => setSoldPrice(e.target.value)} placeholder="Price ₹" className="bg-[var(--bg-base)] border-white/10 text-white font-mono text-sm" data-testid="sold-price" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-[var(--text-muted)] tracking-wider mb-1 block">Bought {swap.to_symbol}</label>
              <div className="grid grid-cols-2 gap-1">
                <Input type="number" value={boughtQty} onChange={(e) => setBoughtQty(e.target.value)} placeholder="Qty" className="bg-[var(--bg-base)] border-white/10 text-white font-mono text-sm" data-testid="bought-qty" />
                <Input type="number" value={boughtPrice} onChange={(e) => setBoughtPrice(e.target.value)} placeholder="Price ₹" className="bg-[var(--bg-base)] border-white/10 text-white font-mono text-sm" data-testid="bought-price" />
              </div>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-[var(--text-muted)] tracking-wider mb-1 block">Notes (optional)</label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. swapped 50% of position, kept rest" className="bg-[var(--bg-base)] border-white/10 text-white text-sm" data-testid="swap-notes" />
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded"
            data-testid="save-executed-btn"
          >
            {saving ? "Saving…" : "Record execution"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ExecutionsList({ executions, onDelete, onStockClick }) {
  if (executions.length === 0) {
    return (
      <div className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-8 text-center text-[var(--text-muted)] text-sm" data-testid="executions-empty">
        <History size={24} className="mx-auto mb-2 opacity-50" />
        No executions yet. Mark a swap as executed from the Swap Suggestions tab to start tracking performance.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="executions-list">
      {executions.map((e) => {
        const delta = e.swap_delta_pnl_pct;
        const deltaColor = delta === undefined ? "text-[var(--text-muted)]" : delta >= 0 ? "text-emerald-400" : "text-red-400";
        return (
          <div key={e.id} className="bg-[var(--bg-surface)] border border-white/10 rounded-lg p-4" data-testid={`execution-${e.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-red-400 font-mono cursor-pointer hover:underline" onClick={() => onStockClick?.(e.from_symbol)}>{e.from_symbol}</span>
                  <ArrowRight size={13} className="text-[var(--text-muted)]" />
                  <span className="text-emerald-400 font-mono cursor-pointer hover:underline" onClick={() => onStockClick?.(e.to_symbol)}>{e.to_symbol}</span>
                  <span className="text-[10px] text-[var(--text-muted)] ml-auto">{new Date(e.executed_at).toLocaleDateString()}</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mt-3 text-[11px]">
                  <div>
                    <div className="text-[var(--text-muted)] uppercase text-[9px] tracking-wider">Sold {e.from_symbol}</div>
                    <div className="font-mono text-white">
                      {e.sold_quantity || "?"} @ ₹{e.sold_price?.toFixed(2) || "?"}
                    </div>
                    {e.from_hypothetical_pnl_pct !== undefined && (
                      <div className={`font-mono text-[10px] mt-0.5 ${e.from_hypothetical_pnl_pct >= 0 ? "text-amber-400" : "text-[var(--text-muted)]"}`}>
                        if held: {e.from_hypothetical_pnl_pct >= 0 ? "+" : ""}{e.from_hypothetical_pnl_pct}%
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[var(--text-muted)] uppercase text-[9px] tracking-wider">Bought {e.to_symbol}</div>
                    <div className="font-mono text-white">
                      {e.bought_quantity || "?"} @ ₹{e.bought_price?.toFixed(2) || "?"}
                    </div>
                    {e.to_actual_pnl_pct !== undefined && (
                      <div className={`font-mono text-[10px] mt-0.5 ${e.to_actual_pnl_pct >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                        actual: {e.to_actual_pnl_pct >= 0 ? "+" : ""}{e.to_actual_pnl_pct}%
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="text-[var(--text-muted)] uppercase text-[9px] tracking-wider">Swap Delta</div>
                    <div className={`font-mono text-base ${deltaColor}`}>
                      {delta === undefined ? "—" : (delta >= 0 ? "+" : "") + delta + "%"}
                    </div>
                    <div className="text-[9px] text-[var(--text-muted)]">new vs old</div>
                  </div>
                </div>
                {e.notes && <div className="text-[10px] text-[var(--text-muted)] italic mt-2">{e.notes}</div>}
              </div>
              <button
                onClick={() => onDelete(e.id)}
                className="p-1.5 text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                data-testid={`delete-exec-${e.id}`}
              >
                <Trash2 size={12} />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
